import { useState, useEffect, useCallback, useMemo } from "react";

/* ═══════════════════════════════════════════
   DATA
═══════════════════════════════════════════ */
const NAV = [
  { id: "hero",       label: "Accueil" },
  { id: "concepts",   label: "Concepts Fondamentaux" },
  { id: "install",    label: "Installation" },
  { id: "workflow",   label: "Cycle de Vie" },
  { id: "commands",   label: "Référence Commandes" },
  { id: "branching",  label: "Gestion des Branches" },
  { id: "github",     label: "GitHub & Collaboration" },
  { id: "practices",  label: "Bonnes Pratiques" },
  { id: "cheatsheet", label: "Cheat Sheet" },
];

const COMMANDS = [
  {
    cat: "Configuration initiale",
    color: "#E8A45A",
    desc: "Paramètres d'identité Git — à définir une seule fois sur chaque machine.",
    items: [
      { cmd: 'git config --global user.name "Votre Nom"', desc: "Définit le nom d'auteur associé à tous vos commits.", ex: '$ git config --global user.name "Alice Martin"' },
      { cmd: 'git config --global user.email "email@domaine.com"', desc: "Définit l'adresse email associée à votre identité Git.", ex: null },
      { cmd: "git config --list", desc: "Affiche l'intégralité de la configuration Git active.", ex: "$ git config --list\nuser.name=Alice Martin\nuser.email=alice@domaine.com\ncore.autocrlf=input" },
      { cmd: 'git config --global core.editor "code --wait"', desc: "Définit VS Code comme éditeur de texte par défaut pour Git.", ex: null },
      { cmd: 'git config --global init.defaultBranch main', desc: "Définit 'main' comme nom de branche par défaut lors de git init.", ex: null },
    ]
  },
  {
    cat: "Initialisation & Clonage",
    color: "#5AB4E8",
    desc: "Démarrer un projet Git ou récupérer un dépôt existant.",
    items: [
      { cmd: "git init", desc: "Initialise un nouveau dépôt Git dans le répertoire courant. Crée le dossier .git/.", ex: "$ git init\nInitialized empty Git repository in /projet/.git/" },
      { cmd: "git clone <url>", desc: "Copie un dépôt distant en local avec la totalité de son historique.", ex: "$ git clone https://github.com/user/repo.git\nCloning into 'repo'...\nremote: Enumerating objects: 128, done." },
      { cmd: "git clone <url> <dossier>", desc: "Clone le dépôt dans un dossier portant un nom personnalisé.", ex: null },
      { cmd: "git clone --depth 1 <url>", desc: "Clone superficiel — ne récupère que le dernier commit. Idéal pour les grands projets.", ex: null },
    ]
  },
  {
    cat: "Staging & Commits",
    color: "#5AE8A4",
    desc: "Le cœur du workflow Git — enregistrer des snapshots de votre code.",
    items: [
      { cmd: "git status", desc: "Affiche l'état du répertoire de travail : fichiers modifiés, indexés et non suivis.", ex: "$ git status\nOn branch main\nChanges not staged for commit:\n  modified:   src/App.js\nUntracked files:\n  .env.example" },
      { cmd: "git add <fichier>", desc: "Ajoute un fichier spécifique à la zone d'index (staging area).", ex: "$ git add src/components/Header.jsx" },
      { cmd: "git add .", desc: "Indexe tous les fichiers modifiés et non suivis du répertoire courant.", ex: null },
      { cmd: "git add -p", desc: "Mode interactif — sélectionne précisément quelles modifications indexer (hunk par hunk).", ex: null },
      { cmd: 'git commit -m "message"', desc: "Crée un commit permanent avec un message descriptif.", ex: '$ git commit -m "feat(auth): implement JWT authentication"\n[main 3a4b5c6] feat(auth): implement JWT authentication\n 3 files changed, 89 insertions(+), 12 deletions(-)' },
      { cmd: 'git commit -am "message"', desc: "Indexe et commite les fichiers déjà suivis en une seule commande.", ex: null },
      { cmd: "git commit --amend", desc: "Modifie le message ou le contenu du dernier commit (avant tout push).", ex: null },
      { cmd: "git diff", desc: "Affiche les différences entre le répertoire de travail et l'index.", ex: null },
      { cmd: "git diff --staged", desc: "Affiche les différences entre l'index et le dernier commit.", ex: null },
    ]
  },
  {
    cat: "Historique & Inspection",
    color: "#A45AE8",
    desc: "Explorer et analyser l'historique d'un dépôt.",
    items: [
      { cmd: "git log", desc: "Affiche l'historique complet des commits avec hash, auteur, date et message.", ex: "$ git log\ncommit 3a4b5c6d...\nAuthor: Alice Martin <alice@domaine.com>\nDate:   Thu Mar 10 09:14:22 2025\n\n    feat(auth): implement JWT authentication" },
      { cmd: "git log --oneline", desc: "Vue condensée de l'historique — un commit par ligne.", ex: "$ git log --oneline\n3a4b5c6 feat(auth): implement JWT\n2b3c4d5 fix(ui): resolve mobile overflow\n1a2b3c4 chore: initial project setup" },
      { cmd: "git log --graph --oneline --all", desc: "Visualise le graphe de branches dans le terminal — très utile pour comprendre les merges.", ex: null },
      { cmd: "git show <hash>", desc: "Affiche le détail complet d'un commit : métadonnées + diff.", ex: null },
      { cmd: "git blame <fichier>", desc: "Identifie l'auteur et le commit responsable de chaque ligne d'un fichier.", ex: null },
      { cmd: "git reflog", desc: "Historique de toutes les opérations HEAD — permet de récupérer des commits perdus.", ex: null },
    ]
  },
  {
    cat: "Gestion des Branches",
    color: "#E85AB4",
    desc: "Créer, naviguer et fusionner des branches de développement.",
    items: [
      { cmd: "git branch", desc: "Liste toutes les branches locales. Un astérisque indique la branche active.", ex: "$ git branch\n* main\n  feature/authentication\n  fix/mobile-layout" },
      { cmd: "git branch <nom>", desc: "Crée une nouvelle branche sans y basculer.", ex: "$ git branch feature/dashboard" },
      { cmd: "git checkout -b <nom>", desc: "Crée une branche et y bascule immédiatement.", ex: "$ git checkout -b feature/dashboard\nSwitched to a new branch 'feature/dashboard'" },
      { cmd: "git switch <branche>", desc: "Bascule vers une branche existante (syntaxe moderne, recommandée).", ex: null },
      { cmd: "git merge <branche>", desc: "Fusionne la branche spécifiée dans la branche courante.", ex: "$ git merge feature/dashboard\nUpdating 3a4b5c6..7e8f9g0\nFast-forward — 4 files changed" },
      { cmd: "git branch -d <nom>", desc: "Supprime une branche locale après fusion.", ex: null },
      { cmd: "git rebase <branche>", desc: "Rejoue les commits sur une nouvelle base — produit un historique linéaire.", ex: null },
    ]
  },
  {
    cat: "Remote & Synchronisation",
    color: "#E87A5A",
    desc: "Connecter le dépôt local à GitHub et synchroniser les changements.",
    items: [
      { cmd: "git remote add origin <url>", desc: "Associe un dépôt distant (GitHub) au dépôt local.", ex: "$ git remote add origin https://github.com/user/repo.git" },
      { cmd: "git remote -v", desc: "Affiche les connexions distantes configurées (fetch et push).", ex: "$ git remote -v\norigin  https://github.com/user/repo.git (fetch)\norigin  https://github.com/user/repo.git (push)" },
      { cmd: "git push origin <branche>", desc: "Envoie les commits locaux vers le dépôt distant.", ex: "$ git push origin main\nTo https://github.com/user/repo.git\n   3a4b5c6..7e8f9g0  main -> main" },
      { cmd: "git push -u origin main", desc: "Envoie et définit la branche upstream par défaut (à faire lors du premier push).", ex: null },
      { cmd: "git pull", desc: "Récupère et fusionne les changements distants dans la branche locale.", ex: "$ git pull\nAlready up to date." },
      { cmd: "git fetch", desc: "Télécharge les données distantes sans les fusionner — plus sûr pour inspecter avant intégration.", ex: null },
    ]
  },
  {
    cat: "Annulation & Correction",
    color: "#E85A5A",
    desc: "Corriger des erreurs et restaurer des états antérieurs.",
    items: [
      { cmd: "git restore <fichier>", desc: "Annule les modifications non indexées d'un fichier.", ex: "$ git restore src/App.js\n# Fichier restauré à son état du dernier commit" },
      { cmd: "git restore --staged <fichier>", desc: "Retire un fichier de l'index sans supprimer ses modifications.", ex: null },
      { cmd: "git reset HEAD~1", desc: "Annule le dernier commit en conservant les modifications locales.", ex: null },
      { cmd: "git reset --hard HEAD~1", desc: "Supprime définitivement le dernier commit et toutes ses modifications. Irréversible.", ex: null },
      { cmd: "git revert <hash>", desc: "Crée un nouveau commit qui inverse les changements d'un commit précédent. Méthode sûre et traçable.", ex: null },
      { cmd: "git stash", desc: "Met de côté les modifications en cours sans les commiter.", ex: "$ git stash\nSaved working directory and index state WIP on main: 3a4b5c6" },
      { cmd: "git stash pop", desc: "Restaure les dernières modifications mises en attente avec stash.", ex: null },
      { cmd: "git stash list", desc: "Liste toutes les entrées de la pile stash.", ex: null },
    ]
  },
  {
    cat: "Commandes Avancées",
    color: "#8A9BB0",
    desc: "Outils puissants pour les workflows complexes et l'inspection approfondie.",
    items: [
      { cmd: "git cherry-pick <hash>", desc: "Applique un commit spécifique sur la branche courante.", ex: null },
      { cmd: "git tag v1.0.0", desc: "Crée un tag annoté pour marquer une version de release.", ex: "$ git tag v1.0.0\n$ git push origin v1.0.0" },
      { cmd: "git bisect start", desc: "Démarre une recherche binaire interactive pour localiser un commit introduisant un bug.", ex: null },
      { cmd: "git shortlog -sn", desc: "Résumé des contributions classées par auteur et nombre de commits.", ex: null },
      { cmd: ".gitignore", desc: "Fichier de patterns définissant les fichiers et dossiers exclus du suivi Git.", ex: "# .gitignore\nnode_modules/\n.env\n.env.local\ndist/\nbuild/\n*.log\n.DS_Store\ncoverage/" },
    ]
  },
];

const CHEATSHEET = [
  { group: "Initialiser", color: "#5AB4E8", cmds: [["git init", "Nouveau dépôt"], ["git clone <url>", "Cloner un dépôt"], ["git status", "État des fichiers"]] },
  { group: "Enregistrer", color: "#5AE8A4", cmds: [["git add .", "Indexer tout"], ['git commit -m "msg"', "Créer un commit"], ["git diff", "Voir les différences"]] },
  { group: "Branches", color: "#E85AB4", cmds: [["git checkout -b nom", "Nouvelle branche"], ["git merge branche", "Fusionner"], ["git branch -d nom", "Supprimer"]] },
  { group: "Synchronisation", color: "#E87A5A", cmds: [["git push origin main", "Envoyer"], ["git pull", "Récupérer et fusionner"], ["git fetch", "Télécharger"]] },
  { group: "Correction", color: "#E85A5A", cmds: [["git restore <f>", "Annuler modification"], ["git reset HEAD~1", "Défaire commit"], ["git stash", "Mettre en attente"]] },
  { group: "Historique", color: "#A45AE8", cmds: [["git log --oneline", "Historique compact"], ["git blame <f>", "Auteur par ligne"], ["git show <hash>", "Détails commit"]] },
];

/* ═══════════════════════════════════════════
   COMPONENTS
═══════════════════════════════════════════ */
function CopyBtn({ text }) {
  const [ok, setOk] = useState(false);
  return (
    <button onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(text); setOk(true); setTimeout(() => setOk(false), 2000); }}
      title="Copier la commande"
      aria-label={`Copier : ${text}`}
      style={{
        flexShrink: 0, width: 30, height: 30, borderRadius: 6,
        background: ok ? "rgba(90,232,164,.15)" : "rgba(255,255,255,.04)",
        border: `1px solid ${ok ? "rgba(90,232,164,.4)" : "rgba(255,255,255,.08)"}`,
        color: ok ? "#5AE8A4" : "rgba(255,255,255,.3)",
        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 12, transition: "all .2s", fontFamily: "monospace",
      }}>
      <span aria-hidden="true">{ok ? "✓" : "⎘"}</span>
      <span className="sr-only">{ok ? "Copié" : "Copier"}</span>
    </button>
  );
}

function Pill({ children, color = "#5AE8A4" }) {
  return (
    <span style={{
      display: "inline-block", padding: "3px 11px", borderRadius: 100,
      fontSize: 10.5, fontWeight: 600, letterSpacing: ".07em", textTransform: "uppercase",
      background: color + "18", color, border: `1px solid ${color}35`,
      fontFamily: "'Geist Mono', 'IBM Plex Mono', monospace",
    }}>{children}</span>
  );
}

function H2({ children, sub, pill }) {
  return (
    <div style={{ marginBottom: 44 }}>
      {pill && <div style={{ marginBottom: 14 }}><Pill>{pill}</Pill></div>}
      <h2 style={{
        fontFamily: "'DM Sans', 'Inter', sans-serif",
        fontSize: "clamp(24px, 3.5vw, 38px)", fontWeight: 700,
        letterSpacing: "-.03em", color: "#F2F2F0",
        margin: "0 0 14px", lineHeight: 1.15,
      }}>{children}</h2>
      {sub && <p style={{ color: "rgba(242,242,240,.42)", fontSize: 15, lineHeight: 1.75, maxWidth: 600, margin: 0, fontWeight: 400 }}>{sub}</p>}
      <div style={{ width: 32, height: 2, borderRadius: 2, background: "linear-gradient(90deg,#5AE8A4,#5AB4E8)", marginTop: 20 }} />
    </div>
  );
}

function CommandCard({ cmd, desc, ex, color }) {
  const [open, setOpen] = useState(false);
  const isDestructive = cmd.includes("--hard") || (cmd.includes("reset") && cmd.includes("HEAD"));
  return (
    <article
      onClick={() => ex && setOpen(o => !o)}
      onKeyDown={e => { if (ex && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setOpen(o => !o); } }}
      tabIndex={ex ? 0 : undefined}
      role={ex ? "button" : undefined}
      aria-expanded={ex ? open : undefined}
      style={{
        background: "rgba(242,242,240,.022)", border: "1px solid rgba(242,242,240,.065)",
        borderLeft: `2px solid ${isDestructive ? "#E85A5A" : color}`,
        borderRadius: 9, padding: "13px 15px", cursor: ex ? "pointer" : "default",
        transition: "background .15s", marginBottom: 7,
      }}
      onMouseEnter={e => e.currentTarget.style.background = "rgba(242,242,240,.04)"}
      onMouseLeave={e => e.currentTarget.style.background = "rgba(242,242,240,.022)"}
    >
      <div style={{ display: "flex", gap: 11, alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5, flexWrap: "wrap" }}>
            <code style={{
              fontFamily: "'Geist Mono', 'IBM Plex Mono', monospace", fontSize: 12.5,
              color: isDestructive ? "#E8907A" : color,
              background: (isDestructive ? "#E85A5A" : color) + "12",
              padding: "2px 8px", borderRadius: 4,
            }}>{cmd}</code>
            {isDestructive && <span style={{ fontSize: 10, color: "#E85A5A", fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase" }}>Destructif</span>}
          </div>
          <p style={{ margin: 0, color: "rgba(242,242,240,.5)", fontSize: 13.5, lineHeight: 1.6 }}>{desc}</p>
          {open && ex && (
            <pre style={{
              marginTop: 10, padding: "12px 14px",
              background: "rgba(0,0,0,.45)", borderRadius: 7,
              fontSize: 11.5, color: "#9EECD0",
              fontFamily: "'Geist Mono', 'IBM Plex Mono', monospace",
              overflowX: "auto", lineHeight: 1.75,
              border: "1px solid rgba(242,242,240,.05)", whiteSpace: "pre-wrap", margin: "10px 0 4px",
            }}>{ex}</pre>
          )}
          {ex && <span aria-hidden="true" style={{ fontSize: 10.5, color: "rgba(242,242,240,.2)", marginTop: 4, display: "block", letterSpacing: ".02em" }}>{open ? "▲ Masquer l'exemple" : "▼ Voir un exemple"}</span>}
        </div>
        <CopyBtn text={cmd} />
      </div>
    </article>
  );
}

/* ═══════════════════════════════════════════
   APP
═══════════════════════════════════════════ */
export default function App() {
  const [active, setActive] = useState("hero");
  const [search, setSearch] = useState("");
  const [navOpen, setNavOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    const s = document.createElement("style");
    s.textContent = `
      *, *::before, *::after { box-sizing: border-box; }
      html, body { margin: 0; padding: 0; scroll-behavior: smooth; }
      ::-webkit-scrollbar { width: 4px; }
      ::-webkit-scrollbar-thumb { background: rgba(242,242,240,.1); border-radius: 4px; }
      @keyframes fadeIn { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
      .fade-in { animation: fadeIn .55s cubic-bezier(.22,1,.36,1) forwards; }
      .sr-only { position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0; }
      *:focus-visible { outline: 2px solid #5AE8A4; outline-offset: 2px; }
      #sidebar { transition: transform .3s cubic-bezier(.4,0,.2,1); }
      #content { transition: margin-left .3s cubic-bezier(.4,0,.2,1); }
      .sidebar-toggle { transition: left .3s cubic-bezier(.4,0,.2,1), background .2s; }
      @media (max-width: 780px) {
        #topbar { display: flex !important; }
        #sidebar { transform: translateX(-100%) !important; transition: transform .28s cubic-bezier(.4,0,.2,1); top: 52px !important; }
        #sidebar.mob-open { transform: translateX(0) !important; }
        #content { margin-left: 0 !important; padding-top: 68px !important; }
        .sidebar-toggle { display: none !important; }
      }
    `;
    document.head.appendChild(s);
    return () => { document.head.removeChild(s); };
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      es => es.forEach(e => { if (e.isIntersecting) setActive(e.target.id); }),
      { threshold: 0.2, rootMargin: "-70px 0px -55% 0px" }
    );
    NAV.forEach(({ id }) => { const el = document.getElementById(id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);

  const go = useCallback(id => { document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); setNavOpen(false); }, []);

  const filtered = useMemo(() => search.trim()
    ? COMMANDS.map(c => ({ ...c, items: c.items.filter(i => i.cmd.toLowerCase().includes(search.toLowerCase()) || i.desc.toLowerCase().includes(search.toLowerCase())) })).filter(c => c.items.length)
    : COMMANDS, [search]);

  const SW = 248;
  const ACC = "#5AE8A4";

  return (
    <div style={{ minHeight: "100vh", background: "#0D0F14", color: "#F2F2F0", fontFamily: "'DM Sans', sans-serif" }}>

      {/* ── Skip to content ── */}
      <a href="#content" className="sr-only" style={{ position: "fixed", top: 0, left: 0, zIndex: 9999, background: "#5AE8A4", color: "#0D0F14", padding: "8px 16px", fontSize: 14, fontWeight: 600, textDecoration: "none" }}
        onFocus={e => { e.currentTarget.style.position = "fixed"; e.currentTarget.style.width = "auto"; e.currentTarget.style.height = "auto"; e.currentTarget.style.clip = "auto"; e.currentTarget.style.overflow = "visible"; }}
        onBlur={e => { e.currentTarget.style.position = "absolute"; e.currentTarget.style.width = "1px"; e.currentTarget.style.height = "1px"; e.currentTarget.style.clip = "rect(0,0,0,0)"; e.currentTarget.style.overflow = "hidden"; }}
      >Aller au contenu principal</a>

      {/* ── Ambient background ── */}
      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0, overflow: "hidden" }}>
        <div style={{ position: "absolute", top: "-25%", left: "-15%", width: "55vw", height: "55vw", background: "radial-gradient(circle, rgba(90,232,164,.04) 0%, transparent 68%)", borderRadius: "50%" }} />
        <div style={{ position: "absolute", bottom: "-15%", right: "-20%", width: "50vw", height: "50vw", background: "radial-gradient(circle, rgba(90,180,232,.035) 0%, transparent 68%)", borderRadius: "50%" }} />
        <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(242,242,240,.012) 1px, transparent 1px), linear-gradient(90deg, rgba(242,242,240,.012) 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
      </div>

      {/* ── Mobile top bar ── */}
      <header id="topbar" style={{
        display: "none", position: "fixed", top: 0, left: 0, right: 0, height: 52, zIndex: 400,
        background: "rgba(13,15,20,.97)", backdropFilter: "blur(24px)",
        borderBottom: "1px solid rgba(242,242,240,.06)",
        padding: "0 18px", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 22, height: 22, borderRadius: 5, background: "linear-gradient(135deg,#5AE8A4,#5AB4E8)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "monospace", fontWeight: 900, fontSize: 11, color: "#0D0F14" }}>G</div>
          <span style={{ fontWeight: 600, fontSize: 14, letterSpacing: "-.01em" }}>Git & GitHub</span>
        </div>
        <button onClick={() => setNavOpen(o => !o)} aria-label={navOpen ? "Fermer le menu" : "Ouvrir le menu"} aria-expanded={navOpen} style={{ background: "rgba(242,242,240,.06)", border: "1px solid rgba(242,242,240,.1)", borderRadius: 7, padding: "5px 10px", color: "#F2F2F0", cursor: "pointer", fontSize: 14 }}>
          <span aria-hidden="true">{navOpen ? "✕" : "☰"}</span>
        </button>
      </header>

      {/* ── Sidebar toggle button ── */}
      <button className="sidebar-toggle" onClick={() => setSidebarOpen(o => !o)} aria-label={sidebarOpen ? "Fermer la barre latérale" : "Ouvrir la barre latérale"} style={{
        position: "fixed", top: 16, left: sidebarOpen ? SW + 10 : 16, zIndex: 350,
        width: 32, height: 32, borderRadius: 8,
        background: "rgba(13,15,20,.9)", backdropFilter: "blur(12px)",
        border: "1px solid rgba(242,242,240,.1)",
        color: "rgba(242,242,240,.5)", cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 16,
      }}
        onMouseEnter={e => { e.currentTarget.style.background = "rgba(242,242,240,.08)"; e.currentTarget.style.color = ACC; }}
        onMouseLeave={e => { e.currentTarget.style.background = "rgba(13,15,20,.9)"; e.currentTarget.style.color = "rgba(242,242,240,.5)"; }}
      >{sidebarOpen ? "◂" : "▸"}</button>

      {/* ── Sidebar ── */}
      <nav id="sidebar" aria-label="Navigation principale" className={navOpen ? "mob-open" : ""} style={{
        position: "fixed", top: 0, left: 0, bottom: 0, width: SW, zIndex: 300,
        background: "rgba(10,12,17,.98)", backdropFilter: "blur(32px)",
        borderRight: "1px solid rgba(242,242,240,.055)",
        display: "flex", flexDirection: "column", overflowY: "auto",
        transform: sidebarOpen ? "translateX(0)" : "translateX(-100%)",
      }}>
        {/* Brand */}
        <div style={{ padding: "28px 22px 22px", borderBottom: "1px solid rgba(242,242,240,.055)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 16 }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: "linear-gradient(135deg,#5AE8A4,#5AB4E8)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 14, color: "#0D0F14", fontFamily: "monospace", flexShrink: 0 }}>G</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14.5, letterSpacing: "-.02em", lineHeight: 1.15 }}>Git & GitHub</div>
              <div style={{ color: "rgba(242,242,240,.28)", fontSize: 10.5, fontFamily: "'IBM Plex Mono',monospace", letterSpacing: ".04em", marginTop: 2 }}>by @dezy</div>
            </div>
          </div>

          {/* Instagram badge */}
          <a href="https://instagram.com/dezy.exe" target="_blank" rel="noopener noreferrer" style={{
            display: "flex", alignItems: "center", gap: 8,
            background: "linear-gradient(135deg, rgba(225,48,108,.12), rgba(193,53,132,.08))",
            border: "1px solid rgba(225,48,108,.25)", borderRadius: 9,
            padding: "9px 13px", textDecoration: "none",
            transition: "all .2s",
          }}
            onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(225,48,108,.5)"}
            onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(225,48,108,.25)"}
          >
            {/* Instagram SVG */}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5" stroke="#E1306C" strokeWidth="2"/>
              <circle cx="12" cy="12" r="4" stroke="#E1306C" strokeWidth="2"/>
              <circle cx="17.5" cy="6.5" r="1.5" fill="#E1306C"/>
            </svg>
            <div>
              <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 11.5, fontWeight: 600, color: "#E1306C", lineHeight: 1 }}>@dezy.exe</div>
              <div style={{ fontSize: 9.5, color: "rgba(242,242,240,.3)", marginTop: 2, letterSpacing: ".03em" }}>Contenu Créé par</div>
            </div>
          </a>
        </div>

        {/* Nav links */}
        <nav style={{ padding: "14px 10px", flex: 1 }}>
          <div style={{ fontSize: 9.5, color: "rgba(242,242,240,.22)", fontFamily: "'IBM Plex Mono',monospace", letterSpacing: ".1em", textTransform: "uppercase", padding: "0 12px", marginBottom: 10 }}>Navigation</div>
          {NAV.map(({ id, label }) => {
            const isActive = active === id;
            return (
              <button key={id} onClick={() => go(id)} style={{
                display: "flex", alignItems: "center", gap: 8,
                width: "100%", padding: "9px 12px", marginBottom: 2,
                background: isActive ? "rgba(90,232,164,.08)" : "transparent",
                border: "none", borderRadius: 7,
                borderLeft: `2px solid ${isActive ? ACC : "transparent"}`,
                color: isActive ? ACC : "rgba(242,242,240,.42)",
                fontSize: 13, fontFamily: "'DM Sans',sans-serif",
                fontWeight: isActive ? 600 : 400, cursor: "pointer",
                textAlign: "left", transition: "all .15s",
              }}>
                {label}
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div style={{ padding: "18px 22px", borderTop: "1px solid rgba(242,242,240,.055)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: ACC }} />
            <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, color: "rgba(242,242,240,.25)" }}>
              {COMMANDS.reduce((a, c) => a + c.items.length, 0)} commandes · {COMMANDS.length} catégories
            </span>
          </div>
          <p style={{ color: "rgba(242,242,240,.18)", fontSize: 10.5, margin: 0, fontFamily: "'IBM Plex Mono',monospace", lineHeight: 1.7 }}>
           © 2026 dezy. All rights reserved.
          </p>
        </div>
      </nav>

      {/* ── Main Content ── */}
      <main id="content" role="main" style={{ marginLeft: sidebarOpen ? SW : 0, padding: "0 clamp(20px,5vw,64px) 100px", position: "relative", zIndex: 1 }}>
        <div style={{ maxWidth: 860, margin: "0 auto" }}>

          {/* ════ HERO ════ */}
          <section id="hero" style={{ paddingTop: "clamp(72px,12vh,130px)", paddingBottom: 80 }}>
            <div className="fade-in">
              

              <h1 style={{
                fontFamily: "'DM Sans',sans-serif", fontSize: "clamp(38px,6.5vw,68px)",
                fontWeight: 700, letterSpacing: "-.04em", lineHeight: 1.06,
                margin: "0 0 22px",
              }}>
                <span style={{ color: "#F2F2F0" }}>Maîtriser</span>{" "}
                <span style={{ background: "linear-gradient(135deg,#5AE8A4 20%,#5AB4E8 80%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Git</span>
                <br />
                <span style={{ color: "rgba(242,242,240,.35)" }}>et</span>{" "}
                <span style={{ background: "linear-gradient(135deg,#5AB4E8 20%,#A45AE8 80%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>GitHub</span>
              </h1>

              <p style={{ color: "rgba(242,242,240,.48)", fontSize: "clamp(14.5px,1.8vw,16.5px)", lineHeight: 1.8, maxWidth: 540, marginBottom: 42, fontWeight: 400 }}>
                Un guide de référence complet — des fondements théoriques aux workflows professionnels.
                Apprenez à versionner, collaborer et déployer avec efficacité.
              </p>

              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <button onClick={() => go("commands")} style={{
                  padding: "12px 26px", borderRadius: 9, border: "none",
                  background: "linear-gradient(135deg,#5AE8A4,#5AB4E8)",
                  color: "#0D0F14", fontWeight: 600, fontSize: 14,
                  fontFamily: "'DM Sans',sans-serif", cursor: "pointer", letterSpacing: ".01em",
                }}>Explorer les commandes →</button>
                <button onClick={() => go("cheatsheet")} style={{
                  padding: "12px 26px", borderRadius: 9,
                  border: "1px solid rgba(242,242,240,.1)",
                  background: "rgba(242,242,240,.03)",
                  color: "rgba(242,242,240,.7)", fontWeight: 500, fontSize: 14,
                  fontFamily: "'DM Sans',sans-serif", cursor: "pointer",
                }}>Cheat Sheet</button>
              </div>

              {/* Stats */}
              <div style={{ display: "flex", gap: 0, marginTop: 56, background: "rgba(242,242,240,.025)", border: "1px solid rgba(242,242,240,.065)", borderRadius: 12, overflow: "hidden", flexWrap: "wrap" }}>
                {[
                  { n: COMMANDS.reduce((a, c) => a + c.items.length, 0), l: "Commandes documentées" },
                  { n: COMMANDS.length, l: "Catégories" },
                  { n: "9", l: "Sections de cours" },
                ].map(({ n, l }, i, arr) => (
                  <div key={l} style={{ flex: "1 1 120px", padding: "22px 18px", textAlign: "center", borderRight: i < arr.length - 1 ? "1px solid rgba(242,242,240,.055)" : "none" }}>
                    <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontWeight: 600, fontSize: 26, color: ACC, letterSpacing: "-.02em" }}>{n}</div>
                    <div style={{ color: "rgba(242,242,240,.3)", fontSize: 11.5, marginTop: 5, letterSpacing: ".03em" }}>{l}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ════ CONCEPTS ════ */}
          <section id="concepts" style={{ paddingTop: 64, paddingBottom: 56 }}>
            <H2 sub="Comprendre la distinction entre Git et GitHub — deux outils complémentaires mais distincts.">
              Concepts Fondamentaux
            </H2>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(270px,1fr))", gap: 14, marginBottom: 36 }}>
              {[
                { color: ACC, title: "Git — Gestionnaire de versions", body: "Logiciel open-source créé par Linus Torvalds en 2005. Git enregistre chaque modification du code source sous forme de snapshots immuables. Il permet de travailler en parallèle, d'expérimenter sans risque et de restaurer n'importe quel état antérieur — entièrement en local, sans connexion réseau." },
                { color: "#5AB4E8", title: "GitHub — Plateforme collaborative", body: "Service cloud qui héberge les dépôts Git et y ajoute une couche de collaboration : pull requests, code review, gestion de tickets, CI/CD via Actions et bien plus. GitHub est devenu le standard de l'industrie avec plus de 100 millions de développeurs actifs." },
                { color: "#A45AE8", title: "Pourquoi apprendre Git ?", body: "Git est la compétence transversale incontournable du développement moderne. Sans lui : pas d'historique, pas de collaboration efficace, pas de déploiement serein. Maîtriser Git distingue un développeur junior d'un professionnel structuré et méthodique." },
              ].map(({ color, title, body }) => (
                <div key={title} style={{ background: "rgba(242,242,240,.022)", border: "1px solid rgba(242,242,240,.065)", borderRadius: 12, padding: "22px 20px" }}>
                  <div style={{ width: 28, height: 3, borderRadius: 2, background: color, marginBottom: 16 }} />
                  <h3 style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 600, fontSize: 15, color, margin: "0 0 10px" }}>{title}</h3>
                  <p style={{ color: "rgba(242,242,240,.48)", fontSize: 13.5, lineHeight: 1.75, margin: 0 }}>{body}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ════ INSTALL ════ */}
          <section id="install" style={{ paddingTop: 64, paddingBottom: 56 }}>
            <H2  sub="Installation et configuration initiale de Git sur les principaux systèmes d'exploitation.">
              Installation & Configuration
            </H2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))", gap: 13, marginBottom: 20 }}>
              {[
                { os: "Windows", icon: "⊞", color: "#5AB4E8", steps: ["Télécharger Git sur git-scm.com", "Lancer l'installateur .exe", "Conserver les paramètres par défaut", "Accéder via Git Bash ou PowerShell"] },
                { os: "Linux", icon: "◈", color: ACC, steps: ["Ubuntu/Debian : sudo apt install git", "Fedora : sudo dnf install git", "Arch : sudo pacman -S git", "Vérifier : git --version"] },
              ].map(({ os, icon, color, steps }) => (
                <div key={os} style={{ background: "rgba(242,242,240,.022)", border: `1px solid ${color}25`, borderRadius: 12, padding: "20px 18px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                    <span style={{ fontSize: 18, color }}>{icon}</span>
                    <span style={{ fontWeight: 600, fontSize: 15, color }}>{os}</span>
                  </div>
                  {steps.map((s, i) => (
                    <div key={i} style={{ display: "flex", gap: 10, marginBottom: 9, alignItems: "flex-start" }}>
                      <span style={{ width: 18, height: 18, borderRadius: "50%", background: color + "20", color, fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontFamily: "monospace", marginTop: 2 }}>{i + 1}</span>
                      <span style={{ color: "rgba(242,242,240,.55)", fontSize: 13.5, lineHeight: 1.5 }}>{s}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <div style={{ background: "rgba(0,0,0,.3)", border: "1px solid rgba(242,242,240,.07)", borderRadius: 11, padding: "18px 20px" }}>
              <p style={{ color: "rgba(242,242,240,.3)", fontSize: 11.5, fontFamily: "'IBM Plex Mono',monospace", margin: "0 0 12px", letterSpacing: ".02em" }}># Configuration obligatoire après installation</p>
              {['git config --global user.name "Votre Nom"', 'git config --global user.email "votre@email.com"', "git config --global init.defaultBranch main"].map(c => (
                <div key={c} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <code style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 12.5, color: "#9EECD0" }}>$ {c}</code>
                  <CopyBtn text={c} />
                </div>
              ))}
            </div>
          </section>

          {/* ════ WORKFLOW ════ */}
          <section id="workflow" style={{ paddingTop: 64, paddingBottom: 56 }}>
            <H2  sub="Les quatre zones de Git et la façon dont les fichiers transitent de l'une à l'autre.">
              Cycle de Vie d'un Fichier Git
            </H2>

            {/* Flow */}
            <div className="flow-diagram" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0, flexWrap: "wrap", marginBottom: 36, padding: "28px 20px", background: "rgba(0,0,0,.2)", borderRadius: 14, border: "1px solid rgba(242,242,240,.06)" }}>
              {[
                { z: "Working Directory", sub: "Fichiers locaux", c: "#E8A45A" },
                { a: "git add" },
                { z: "Staging Area", sub: "Zone d'index", c: ACC },
                { a: "git commit" },
                { z: "Local Repository", sub: "Historique local", c: "#A45AE8" },
                { a: "git push" },
                { z: "Remote (GitHub)", sub: "Dépôt partagé", c: "#5AB4E8" },
              ].map((item, i) =>
                item.a ? (
                  <div key={i} className="flow-arrow" style={{ padding: "0 6px", textAlign: "center" }}>
                    <code style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, color: ACC, display: "block", marginBottom: 3 }}>{item.a}</code>
                    <span className="flow-arrow-icon" style={{ color: "rgba(242,242,240,.25)", fontSize: 16 }}>→</span>
                  </div>
                ) : (
                  <div key={i} className="flow-box" style={{ background: item.c + "0F", border: `1px solid ${item.c}30`, borderRadius: 10, padding: "12px 14px", textAlign: "center", minWidth: 110 }}>
                    <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontWeight: 600, fontSize: 11, color: item.c, lineHeight: 1.3, marginBottom: 3 }}>{item.z}</div>
                    <div style={{ color: "rgba(242,242,240,.3)", fontSize: 10 }}>{item.sub}</div>
                  </div>
                )
              )}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))", gap: 13 }}>
              {[
                { n: "01", title: "Working Directory", c: "#E8A45A", cmd: "git status", body: "Répertoire de travail actif. Lorsqu'un fichier est modifié, Git le détecte mais ne l'enregistre pas encore. État : Untracked ou Modified." },
                { n: "02", title: "Staging Area", c: ACC, cmd: "git add <fichier>", body: "Zone d'index intermédiaire. Vous sélectionnez précisément quelles modifications inclure dans le prochain commit. État : Staged." },
                { n: "03", title: "Local Repository", c: "#A45AE8", cmd: "git commit -m 'msg'", body: "Base de données locale dans .git/. Chaque commit est un snapshot permanent identifié par un hash SHA-1 unique et immuable." },
                { n: "04", title: "Remote Repository", c: "#5AB4E8", cmd: "git push origin main", body: "Dépôt hébergé sur GitHub. Partagé avec l'équipe et accessible depuis n'importe où. Constitue la source de vérité du projet." },
              ].map(({ n, title, c, cmd, body }) => (
                <div key={n} style={{ background: "rgba(242,242,240,.022)", border: "1px solid rgba(242,242,240,.065)", borderRadius: 12, padding: "20px 18px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 11 }}>
                    <span style={{ width: 26, height: 26, borderRadius: "50%", background: c, color: "#0D0F14", fontFamily: "'IBM Plex Mono',monospace", fontWeight: 700, fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center" }}>{n}</span>
                    <span style={{ fontWeight: 600, fontSize: 13.5, color: c }}>{title}</span>
                  </div>
                  <p style={{ color: "rgba(242,242,240,.48)", fontSize: 13, lineHeight: 1.7, margin: "0 0 11px" }}>{body}</p>
                  <code style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 11.5, color: c, background: c + "12", padding: "2px 8px", borderRadius: 4 }}>{cmd}</code>
                </div>
              ))}
            </div>
          </section>

          {/* ════ COMMANDS ════ */}
          <section id="commands" style={{ paddingTop: 64, paddingBottom: 56 }}>
            <H2  sub="Documentation complète des commandes Git. Cliquez sur une entrée pour afficher un exemple d'utilisation.">
              Référence des Commandes
            </H2>

            <div role="search" style={{ display: "flex", alignItems: "center", gap: 10, background: "rgba(242,242,240,.03)", border: "1px solid rgba(242,242,240,.09)", borderRadius: 10, padding: "10px 15px", marginBottom: 34 }}>
              <label htmlFor="cmd-search" style={{ color: "rgba(242,242,240,.25)", fontSize: 14, fontFamily: "monospace", lineHeight: 1 }}>⌕</label>
              <input id="cmd-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher une commande ou une description..."
                aria-label="Rechercher une commande Git"
                style={{ background: "none", border: "none", outline: "none", color: "#F2F2F0", fontSize: 13.5, flex: 1, fontFamily: "'DM Sans',sans-serif" }} />
              {search && <button onClick={() => setSearch("")} aria-label="Effacer la recherche" style={{ background: "none", border: "none", color: "rgba(242,242,240,.25)", cursor: "pointer", fontSize: 13 }}>✕</button>}
            </div>

            {filtered.length === 0 ? (
              <div style={{ textAlign: "center", padding: "48px 20px", color: "rgba(242,242,240,.25)" }}>
                <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 28, marginBottom: 12 }}>◌</div>
                <p style={{ fontSize: 14 }}>Aucun résultat pour <em>"{search}"</em></p>
              </div>
            ) : filtered.map(({ cat, color, desc, items }) => (
              <div key={cat} style={{ marginBottom: 38 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 6 }}>
                  <div style={{ width: 2, height: 16, background: color, borderRadius: 1, flexShrink: 0, marginBottom: -1, alignSelf: "center" }} />
                  <h3 style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 600, fontSize: 15.5, margin: 0, color: "rgba(242,242,240,.88)" }}>{cat}</h3>
                  <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, color: "rgba(242,242,240,.2)", background: "rgba(242,242,240,.04)", padding: "2px 7px", borderRadius: 8 }}>{items.length}</span>
                </div>
                {desc && <p style={{ color: "rgba(242,242,240,.32)", fontSize: 13, margin: "0 0 12px 12px", lineHeight: 1.6 }}>{desc}</p>}
                {items.map(i => <CommandCard key={i.cmd} {...i} color={color} />)}
              </div>
            ))}
          </section>

          {/* ════ BRANCHING ════ */}
          <section id="branching" style={{ paddingTop: 64, paddingBottom: 56 }}>
            <H2 sub="La gestion des branches est l'un des atouts majeurs de Git. Maîtriser cette stratégie est essentiel en environnement professionnel.">
              Gestion des Branches
            </H2>

            <div style={{ background: "rgba(0,0,0,.25)", border: "1px solid rgba(242,242,240,.07)", borderRadius: 12, padding: "22px 22px", marginBottom: 28, overflowX: "auto" }}>
              <p style={{ fontFamily: "'IBM Plex Mono',monospace", color: "rgba(242,242,240,.25)", fontSize: 11, margin: "0 0 10px" }}># Représentation d'un workflow Git structuré</p>
              <pre style={{ fontFamily: "'IBM Plex Mono',monospace", color: "#9EECD0", fontSize: 12, margin: 0, lineHeight: 2, whiteSpace: "pre" }}>
{`  main      ●─────────────────────────────────●──────● (v2.0)
            │                                 ↑      ↑
  develop   ●────●────●────●────────────────●       │
                 │    │    │                         │
  feat/auth      ●────●    │  (PR → merge)           │
                      │                              │
  feat/ui            ●────●  (PR → merge)            │
                                                     │
  hotfix/cve                    ●────────────────────●`}
              </pre>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))", gap: 12, marginBottom: 24 }}>
              {[
                { name: "main", c: ACC, icon: "◉", desc: "Branche de production — stable, protégée, déployable à tout instant. Aucun commit direct. Chaque merge implique une Pull Request validée." },
                { name: "develop", c: "#5AB4E8", icon: "◎", desc: "Branche d'intégration. Reçoit les features terminées avant leur passage en production. Peut être instable temporairement." },
                { name: "feature/nom", c: "#A45AE8", icon: "◈", desc: "Une branche par fonctionnalité, créée depuis develop. Isolée et expérimentale. Fusionnée via Pull Request après revue de code." },
                { name: "hotfix/nom", c: "#E85A5A", icon: "◆", desc: "Correction urgente d'un bug critique en production. Créée depuis main, fusionnée dans main ET develop pour maintenir la cohérence." },
                { name: "release/v1.x", c: "#E8A45A", icon: "◇", desc: "Branche de préparation d'une release. Derniers ajustements, correction de bugs mineurs, bump de version. Ni nouvelles features." },
                { name: "chore/nom", c: "#8A9BB0", icon: "□", desc: "Tâches de maintenance : mise à jour de dépendances, refactoring, amélioration de la documentation ou des outils de build." },
              ].map(({ name, c, icon, desc }) => (
                <div key={name} style={{ background: "rgba(242,242,240,.022)", border: `1px solid ${c}20`, borderRadius: 11, padding: "17px 16px" }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 9 }}>
                    <span style={{ color: c, fontSize: 12 }}>{icon}</span>
                    <code style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 11.5, color: c, background: c + "12", padding: "2px 7px", borderRadius: 4 }}>{name}</code>
                  </div>
                  <p style={{ color: "rgba(242,242,240,.48)", fontSize: 13, lineHeight: 1.65, margin: 0 }}>{desc}</p>
                </div>
              ))}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(290px,1fr))", gap: 13 }}>
              {[
                { t: "git merge — Fusion", c: ACC, badge: "Recommandé pour les équipes", body: "Crée un commit de fusion qui unit deux branches en préservant l'historique complet. Chaque branche reste traçable. Privilégiez cette approche pour les merges de features et les Pull Requests." },
                { t: "git rebase — Réécriture", c: "#A45AE8", badge: "Branches privées uniquement", body: "Rejoue les commits sur une nouvelle base, produisant un historique linéaire sans commits de merge. Puissant mais destructif : à n'utiliser que sur des branches locales non partagées." },
              ].map(({ t, c, badge, body }) => (
                <div key={t} style={{ background: c + "07", border: `1px solid ${c}22`, borderRadius: 12, padding: "22px 20px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 11, flexWrap: "wrap" }}>
                    <h4 style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 600, fontSize: 14.5, color: c, margin: 0 }}>{t}</h4>
                    <Pill color={c}>{badge}</Pill>
                  </div>
                  <p style={{ color: "rgba(242,242,240,.5)", fontSize: 13.5, lineHeight: 1.7, margin: 0 }}>{body}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ════ GITHUB ════ */}
          <section id="github" style={{ paddingTop: 64, paddingBottom: 56 }}>
            <H2 sub="GitHub enrichit Git d'une couche collaborative et d'automatisation indispensable aux équipes modernes.">
              GitHub & Fonctionnalités Clés
            </H2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))", gap: 13, marginBottom: 32 }}>
              {[
                { t: "Pull Request", c: "#E85AB4", desc: "Mécanisme central de collaboration. Proposition de fusion soumise à la revue de code de l'équipe — commentaires, approbations, demandes de modifications. Traçabilité totale des décisions." },
                { t: "Issues & Projects", c: "#E8A45A", desc: "Gestionnaire de tickets intégré. Tickets référençables dans les commits (#42), tableaux Kanban, jalons et labels pour organiser le backlog du projet." },
                { t: "GitHub Actions", c: ACC, desc: "Plateforme CI/CD native. Automatisez tests unitaires, linting, builds et déploiements à chaque événement Git via des workflows YAML déclaratifs." },
                { t: "GitHub Pages", c: "#5AB4E8", desc: "Hébergement gratuit de sites statiques depuis un dépôt. Idéal pour les documentations, portfolios et sites de projets open-source." },
                { t: "Fork & Contribution", c: "#A45AE8", desc: "Copiez un dépôt dans votre espace pour le modifier librement, puis proposez vos améliorations via Pull Request. C'est le modèle de contribution open-source universel." },
                { t: "GitHub Copilot", c: "#E87A5A", desc: "Assistant IA intégré qui génère du code, propose des completions contextuelles et explique du code existant. Alimenté par OpenAI, directement dans votre éditeur." },
                { t: "Gist", c: "#5AE8A4", desc: "Partage rapide de snippets, scripts ou configurations sans créer un dépôt complet. Versionné Git, consultable et forkable par la communauté." },
                { t: "Discussions & Wiki", c: "#8A9BB0", desc: "Forum communautaire intégré au dépôt pour les Q&A et annonces. Le Wiki centralise la documentation longue du projet (architecture, décisions techniques)." },
              ].map(({ t, c, desc }) => (
                <div key={t} style={{ background: "rgba(242,242,240,.022)", border: "1px solid rgba(242,242,240,.065)", borderRadius: 11, padding: "18px 16px", transition: "border-color .2s" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = c + "45"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(242,242,240,.065)"}
                >
                  <div style={{ width: 24, height: 2, borderRadius: 1, background: c, marginBottom: 13 }} />
                  <h3 style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 600, fontSize: 14, color: c, margin: "0 0 9px" }}>{t}</h3>
                  <p style={{ color: "rgba(242,242,240,.48)", fontSize: 13, lineHeight: 1.7, margin: 0 }}>{desc}</p>
                </div>
              ))}
            </div>

            {/* Conventional Commits */}
            <div style={{ background: "rgba(242,242,240,.022)", border: "1px solid rgba(242,242,240,.065)", borderRadius: 12, padding: "24px 22px" }}>
              <h3 style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 600, fontSize: 15.5, color: ACC, margin: "0 0 8px" }}>Conventional Commits — Standard de messages</h3>
              <p style={{ color: "rgba(242,242,240,.42)", fontSize: 13.5, margin: "0 0 18px", lineHeight: 1.7 }}>
                Format standardisé de messages de commit, lisible par les humains et les outils automatisés (changelogs, SemVer).<br />
                Syntaxe : <code style={{ fontFamily: "'IBM Plex Mono',monospace", color: "#9EECD0", fontSize: 12 }}>type(scope): description concise</code>
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18 }}>
                {[
                  { t: "feat", d: "Nouvelle fonctionnalité", c: ACC },
                  { t: "fix", d: "Correction de bug", c: "#E85A5A" },
                  { t: "docs", d: "Documentation", c: "#5AB4E8" },
                  { t: "style", d: "Formatage", c: "#A45AE8" },
                  { t: "refactor", d: "Refactoring", c: "#E8A45A" },
                  { t: "test", d: "Tests", c: "#E85AB4" },
                  { t: "chore", d: "Maintenance", c: "#8A9BB0" },
                  { t: "perf", d: "Performance", c: "#5AE8C8" },
                ].map(({ t, d, c }) => (
                  <div key={t} style={{ display: "flex", gap: 7, alignItems: "center", background: c + "10", border: `1px solid ${c}28`, borderRadius: 7, padding: "6px 11px" }}>
                    <code style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 11.5, color: c, fontWeight: 600 }}>{t}:</code>
                    <span style={{ color: "rgba(242,242,240,.42)", fontSize: 12 }}>{d}</span>
                  </div>
                ))}
              </div>
              <div style={{ background: "rgba(0,0,0,.3)", borderRadius: 8, padding: "13px 16px" }}>
                {[
                  'git commit -m "feat(auth): implement OAuth2 with Google provider"',
                  'git commit -m "fix(api): handle 404 error on missing user endpoint"',
                  'git commit -m "docs(readme): update deployment instructions for v2"',
                ].map(c => (
                  <div key={c} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <code style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 12, color: "#9EECD0" }}>$ {c}</code>
                    <CopyBtn text={c} />
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ════ PRACTICES ════ */}
          <section id="practices" style={{ paddingTop: 64, paddingBottom: 56 }}>
            <H2 sub="Les pratiques qui différencient un code base maintenu d'un projet chaotique.">
              Bonnes Pratiques
            </H2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(270px,1fr))", gap: 12 }}>
              {[
                { n: "01", c: ACC, t: "Commits atomiques", body: "Un commit doit encapsuler une seule modification logique. Des commits granulaires facilitent la revue, le revert ciblé et la compréhension de l'historique." },
                { n: "02", c: "#E85A5A", t: "Protéger la branche main", body: "Activez les protections de branche sur GitHub : revue obligatoire, statuts CI requis, push direct interdit. Main représente l'état de production." },
                { n: "03", c: "#E8A45A", t: "Configurer .gitignore dès le départ", body: "Définissez votre .gitignore avant le premier commit. Ne versionner jamais : node_modules/, .env, secrets, artefacts de build ni fichiers système." },
                { n: "04", c: "#5AB4E8", t: "Synchroniser avant de pousser", body: "Exécutez git pull (ou git fetch + rebase) avant tout git push. Cette habitude prévient les conflits inutiles et maintient un historique propre." },
                { n: "05", c: "#A45AE8", t: "Rédiger des messages significatifs", body: "Un bon message explique le POURQUOI, pas le quoi (visible dans le diff). Adoptez Conventional Commits pour une lisibilité machine et humaine optimale." },
                { n: "06", c: "#E85AB4", t: "Inspecter avant de commiter", body: "Exécutez git diff --staged systématiquement avant chaque commit pour éviter les console.log, credentials ou fichiers non intentionnels." },
                { n: "07", c: "#E87A5A", t: "Branches à durée de vie courte", body: "Une feature branch doit vivre 1 à 3 jours maximum. Les branches longues engendrent des conflits de merge complexes et ralentissent les livraisons." },
                { n: "08", c: "#5AE8A4", t: "Adopter le Semantic Versioning", body: "Utilisez git tag pour marquer chaque release (v1.2.3). Respectez SemVer : MAJOR (breaking), MINOR (feature), PATCH (bugfix). Essentiel en production." },
              ].map(({ n, c, t, body }) => (
                <div key={n} style={{ background: "rgba(242,242,240,.022)", border: "1px solid rgba(242,242,240,.065)", borderRadius: 12, padding: "20px 18px", position: "relative", overflow: "hidden" }}>
                  <div style={{ position: "absolute", top: 12, right: 14, fontFamily: "'IBM Plex Mono',monospace", fontWeight: 700, fontSize: 28, color: "rgba(242,242,240,.04)", lineHeight: 1 }}>{n}</div>
                  <div style={{ width: 20, height: 2, borderRadius: 1, background: c, marginBottom: 12 }} />
                  <h3 style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 600, fontSize: 14, color: "rgba(242,242,240,.85)", margin: "0 0 9px" }}>{t}</h3>
                  <p style={{ color: "rgba(242,242,240,.45)", fontSize: 13.5, lineHeight: 1.7, margin: 0 }}>{body}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ════ CHEAT SHEET ════ */}
          <section id="cheatsheet" style={{ paddingTop: 64, paddingBottom: 56 }}>
            <H2 sub="Synthèse des commandes essentielles — à consulter au quotidien.">
              Cheat Sheet
            </H2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(250px,1fr))", gap: 13, marginBottom: 40 }}>
              {CHEATSHEET.map(({ group, color, cmds }) => (
                <div key={group} style={{ background: "rgba(242,242,240,.022)", border: `1px solid ${color}22`, borderRadius: 12, overflow: "hidden" }}>
                  <div style={{ padding: "11px 16px", borderBottom: `1px solid ${color}18`, display: "flex", alignItems: "center", gap: 8, background: color + "0A" }}>
                    <div style={{ width: 7, height: 7, borderRadius: "50%", background: color }} />
                    <span style={{ fontWeight: 600, fontSize: 12.5, color }}>{group}</span>
                  </div>
                  {cmds.map(([cmd, d]) => (
                    <div key={cmd} style={{ padding: "8px 16px", borderBottom: "1px solid rgba(242,242,240,.035)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                      <div>
                        <code style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 11, color, display: "block", marginBottom: 2 }}>{cmd}</code>
                        <span style={{ fontSize: 11.5, color: "rgba(242,242,240,.35)" }}>{d}</span>
                      </div>
                      <CopyBtn text={cmd} />
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* CTA + Crédit */}
            <div style={{ background: "linear-gradient(135deg, rgba(90,232,164,.06) 0%, rgba(90,180,232,.06) 100%)", border: "1px solid rgba(90,232,164,.18)", borderRadius: 16, padding: "40px 32px", textAlign: "center" }}>
              <h3 style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 700, fontSize: "clamp(20px,3vw,26px)", margin: "0 0 12px", letterSpacing: "-.025em" }}>
                Vous êtes prêt à coder professionnellement.
              </h3>
              <p style={{ color: "rgba(242,242,240,.48)", fontSize: 15, lineHeight: 1.75, maxWidth: 520, margin: "0 auto 30px" }}>
                La pratique régulière est la seule voie vers la maîtrise. Créez votre premier dépôt GitHub, versionner chaque projet personnel et progressez chaque jour.
              </p>
              <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap", marginBottom: 36 }}>
                {[{ l: "github.com", u: "https://github.com", d: "Créer un compte", c: ACC }, { l: "git-scm.com", u: "https://git-scm.com", d: "Documentation officielle", c: "#5AB4E8" }, { l: "learngitbranching.js.org", u: "https://learngitbranching.js.org", d: "Apprendre visuellement", c: "#A45AE8" }].map(({ l, u, d, c }) => (
                  <a key={l} href={u} target="_blank" rel="noopener noreferrer" style={{ background: c + "12", border: `1px solid ${c}28`, borderRadius: 9, padding: "10px 16px", textAlign: "center", textDecoration: "none", transition: "border-color .2s, transform .2s", cursor: "pointer" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = c + "60"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = c + "28"; e.currentTarget.style.transform = "translateY(0)"; }}
                  >
                    <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 11.5, color: c, fontWeight: 600 }}>{l}</div>
                    <div style={{ color: "rgba(242,242,240,.3)", fontSize: 11, marginTop: 3 }}>{d}</div>
                  </a>
                ))}
              </div>

              {/* Instagram credit */}
              <a href="https://instagram.com/dezy.exe" target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "rgba(225,48,108,.08)", border: "1px solid rgba(225,48,108,.22)", borderRadius: 10, padding: "12px 20px", textDecoration: "none", transition: "border-color .2s, transform .2s", cursor: "pointer" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(225,48,108,.5)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(225,48,108,.22)"; e.currentTarget.style.transform = "translateY(0)"; }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" stroke="#E1306C" strokeWidth="2"/>
                  <circle cx="12" cy="12" r="4" stroke="#E1306C" strokeWidth="2"/>
                  <circle cx="17.5" cy="6.5" r="1.5" fill="#E1306C"/>
                </svg>
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 13, fontWeight: 600, color: "#E1306C" }}>@dezy.exe</div>
                  <div style={{ fontSize: 11, color: "rgba(242,242,240,.35)", marginTop: 2 }}>Contenu éducatif · Suivez pour plus de ressources</div>
                </div>
              </a>
            </div>
          </section>

        </div>
      </main>

      {navOpen && <div onClick={() => setNavOpen(false)} role="presentation" aria-hidden="true" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 200 }} />}

      <style>{`
        @media (max-width: 780px) {
          #topbar { display: flex !important; }
          #sidebar { top: 52px !important; }
          #content { margin-left: 0 !important; padding-top: 68px !important; }
          .sidebar-toggle { display: none !important; }
        }
        @media (max-width: 600px) {
          .flow-diagram {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 0 !important;
            padding: 20px 16px !important;
          }
          .flow-box {
            min-width: unset !important;
            width: 100% !important;
            padding: 14px 16px !important;
          }
          .flow-arrow {
            padding: 8px 0 !important;
          }
          .flow-arrow-icon {
            display: inline-block;
            transform: rotate(90deg);
          }
        }
      `}</style>
    </div>
  );
}